/**
 * The passphrase enrolment and the key it derives.
 *
 * WebAuthn is not universal, and on a browser with no platform authenticator
 * the native model is permanently `unavailable` — which would leave Journal
 * Lock unreachable rather than merely weaker. So the web build offers a
 * passphrase as a second enrolment for the same gate, and, unlike the native
 * app, uses it for something the platform cannot otherwise provide: a key.
 *
 * The contract is `02-ARCHITECTURE.md` §6.3, and every number in it is load
 * bearing:
 *
 * - PBKDF2-HMAC-SHA-256, ≥ 600 000 iterations, a 16-byte per-install random
 *   salt, deriving a 256-bit AES-GCM key. The salt is stored; the passphrase is
 *   not, in any form — there is no verifier derived from it, no hash, no hint.
 * - AES-GCM with a fresh 96-bit IV per record, and the record's key bound as
 *   additional authenticated data, so a ciphertext cannot be moved from one
 *   record to another. Without the AAD binding, an attacker with write access
 *   to the store could swap one entry's payload for another's and the decrypt
 *   would succeed.
 * - The derived key is created **non-extractable** and lives only in memory for
 *   the foreground session. `drop()` is called on `foregroundLost`, the same
 *   event that revokes the lease. It is never written anywhere.
 *
 * What this does not do, stated because the privacy copy has to match: it does
 * not defeat an offline guess. 600 000 iterations raises the cost of one; there
 * is no server to rate-limit against, and a weak passphrase is a weak
 * passphrase. It also does not protect index scalars — tag names and entry
 * timing stay in the clear so a list view does not require a full decrypt
 * (§6.4). Both facts belong in the copy, not only in this comment.
 */

import type { ByteArray } from "./bytes";
import { bytesEqual, decodeBase64Url, encodeBase64Url, encodeUtf8 } from "./bytes";

/** Where the per-install salt lives. Public by design — a salt is not a secret. */
export const PASSPHRASE_SALT_KEY = "journal.lock.passphraseSalt";
/**
 * The enrolment check record: a known plaintext under the derived key. It is
 * how a wrong passphrase is told from a right one without storing anything
 * derived from the passphrase itself — AES-GCM's own authentication tag does
 * the work, so the check is a decrypt that either authenticates or does not.
 */
export const PASSPHRASE_CHECK_KEY = "journal.lock.passphraseCheck";

export const PASSPHRASE_PBKDF2_ITERATIONS = 600_000;
export const PASSPHRASE_SALT_BYTES = 16;
/** 96 bits — the only IV length AES-GCM is specified for without extra rules. */
export const PASSPHRASE_IV_BYTES = 12;
export const PASSPHRASE_KEY_BITS = 256;

/**
 * The plaintext of the check record, and its own AAD label.
 *
 * A constant rather than random bytes: the record proves the key is right, and
 * there is nothing to gain from the plaintext being unknown to an attacker who
 * can read the ciphertext anyway.
 */
const CHECK_PLAINTEXT = encodeUtf8("fighting-form/journal-lock/v1");
export const PASSPHRASE_CHECK_AAD = "journal.lock.check";

/** The store this vault keeps its salt and check record in. */
export interface PassphraseStore {
  read(key: string): string | undefined;
  write(key: string, value: string): boolean;
  remove(key: string): void;
}

export interface PassphraseCrypto {
  readonly subtle: SubtleCrypto;
  randomBytes(length: number): ByteArray;
}

export function browserPassphraseCrypto(): PassphraseCrypto {
  return {
    subtle: globalThis.crypto.subtle,
    randomBytes: (length) => globalThis.crypto.getRandomValues(new Uint8Array(length)),
  };
}

/**
 * Why an unlock did not produce a key.
 *
 * `wrongPassphrase` and `unavailable` are separate because the recoveries
 * differ: one is "try again", the other is "this browser cannot do it at all"
 * and no amount of retyping will help.
 */
export type PassphraseUnlockOutcome =
  | "unlocked"
  | "wrongPassphrase"
  | "notEnrolled"
  | "unavailable";

export type PassphraseEnrolOutcome = "enrolled" | "rejected" | "unavailable";

export interface PassphraseVaultOptions {
  readonly store: PassphraseStore;
  readonly crypto?: PassphraseCrypto;
  /**
   * Overridable **only** so tests can derive keys in milliseconds instead of
   * seconds. The default is the contract value and no production path passes
   * this. A lower value is a real weakening, which is why it is named after the
   * cost rather than after the test that wanted it.
   */
  readonly iterations?: number;
}

export class PassphraseKeyVault {
  readonly #store: PassphraseStore;
  readonly #crypto: PassphraseCrypto;
  readonly #iterations: number;
  #key: CryptoKey | null = null;

  constructor(options: PassphraseVaultOptions) {
    this.#store = options.store;
    this.#crypto = options.crypto ?? browserPassphraseCrypto();
    this.#iterations = options.iterations ?? PASSPHRASE_PBKDF2_ITERATIONS;
  }

  /**
   * Whether a passphrase has ever been set on this install.
   *
   * Both the salt and the check record are required: a salt on its own cannot
   * tell a right passphrase from a wrong one, so treating it as an enrolment
   * would mean the first wrong guess silently became the new passphrase.
   */
  isEnrolled(): boolean {
    return (
      this.#storedSalt() !== null &&
      this.#storedEnvelope(PASSPHRASE_CHECK_KEY) !== null
    );
  }

  /** True while a derived key is held in memory for this foreground session. */
  isUnlocked(): boolean {
    return this.#key !== null;
  }

  async enrol(passphrase: string): Promise<PassphraseEnrolOutcome> {
    // An empty passphrase is not an enrolment: it derives a key anybody can
    // derive, and storing a check record for it would report the vault as
    // protected when it is not. No other policy is imposed here — inventing a
    // length or complexity rule the spec does not state would be a product
    // decision made in an adapter.
    if (passphrase === "") return "rejected";

    const salt = this.#crypto.randomBytes(PASSPHRASE_SALT_BYTES);
    let key: CryptoKey;
    try {
      key = await this.#derive(passphrase, salt);
    } catch {
      return "unavailable";
    }

    const envelope = await this.#seal(key, PASSPHRASE_CHECK_AAD, CHECK_PLAINTEXT);
    if (envelope === null) return "unavailable";

    // Salt first, then the check record. The reverse order would leave a check
    // record that no salt can ever reproduce a key for, and `isEnrolled()`
    // would then be false while an undecryptable record sat in the store.
    if (!this.#store.write(PASSPHRASE_SALT_KEY, encodeBase64Url(salt))) {
      return "unavailable";
    }
    if (!this.#store.write(PASSPHRASE_CHECK_KEY, encodeBase64Url(envelope))) {
      this.#store.remove(PASSPHRASE_SALT_KEY);
      return "unavailable";
    }

    this.#key = key;
    return "enrolled";
  }

  async unlock(passphrase: string): Promise<PassphraseUnlockOutcome> {
    const salt = this.#storedSalt();
    const check = this.#storedEnvelope(PASSPHRASE_CHECK_KEY);
    if (salt === null || check === null) return "notEnrolled";

    let key: CryptoKey;
    try {
      key = await this.#derive(passphrase, salt);
    } catch {
      return "unavailable";
    }

    const opened = await this.#open(key, PASSPHRASE_CHECK_AAD, check);
    // A failed open is an authentication failure, which for this record can
    // only mean the derived key is wrong — the record was written by this app
    // with these parameters. So it is reported as a wrong passphrase rather
    // than as a corrupt store, and the key is not retained.
    if (opened === null || !bytesEqual(opened, CHECK_PLAINTEXT)) {
      return "wrongPassphrase";
    }

    this.#key = key;
    return "unlocked";
  }

  /**
   * Releases the derived key.
   *
   * Called on `foregroundLost`, alongside revoking the lease and dropping the
   * library. It is the only thing standing between a backgrounded tab and a key
   * that can decrypt every entry — and it is a foreground-lifetime mitigation
   * only: while the tab is open and revealed, the key is in memory by
   * definition (§6.6).
   */
  drop(): void {
    this.#key = null;
  }

  /**
   * Removes the enrolment entirely. The records encrypted under the old key
   * become unreadable, which is why nothing calls this as part of an ordinary
   * flow — it is the deliberate "I am starting over" action.
   */
  forget(): void {
    this.drop();
    this.#store.remove(PASSPHRASE_SALT_KEY);
    this.#store.remove(PASSPHRASE_CHECK_KEY);
  }

  /**
   * Encrypts a record payload. `recordKey` is bound as additional authenticated
   * data, so the ciphertext only ever decrypts back under the record it was
   * written for.
   *
   * `null` means there was no key — the session is locked — rather than an
   * error, because "locked" is the ordinary state and a throw would make every
   * caller wrap it.
   */
  async seal(recordKey: string, plaintext: ByteArray): Promise<ByteArray | null> {
    if (this.#key === null) return null;
    return this.#seal(this.#key, recordKey, plaintext);
  }

  /** The inverse. `null` for no key, a wrong record key, or a tampered payload. */
  async open(recordKey: string, envelope: ByteArray): Promise<ByteArray | null> {
    if (this.#key === null) return null;
    return this.#open(this.#key, recordKey, envelope);
  }

  async #derive(passphrase: string, salt: ByteArray): Promise<CryptoKey> {
    const material = await this.#crypto.subtle.importKey(
      "raw",
      encodeUtf8(passphrase),
      "PBKDF2",
      false,
      ["deriveKey"],
    );
    return this.#crypto.subtle.deriveKey(
      {
        name: "PBKDF2",
        salt: salt as BufferSource,
        iterations: this.#iterations,
        hash: "SHA-256",
      },
      material,
      { name: "AES-GCM", length: PASSPHRASE_KEY_BITS },
      // Non-extractable. There is no legitimate reason for this key to become
      // bytes the page can read, and `extractable: true` is how a key ends up
      // in a diagnostic dump.
      false,
      ["encrypt", "decrypt"],
    );
  }

  async #seal(
    key: CryptoKey,
    recordKey: string,
    plaintext: ByteArray,
  ): Promise<ByteArray | null> {
    const iv = this.#crypto.randomBytes(PASSPHRASE_IV_BYTES);
    try {
      const ciphertext = await this.#crypto.subtle.encrypt(
        { name: "AES-GCM", iv: iv as BufferSource, additionalData: encodeUtf8(recordKey) },
        key,
        plaintext as BufferSource,
      );
      // The IV is prefixed rather than stored beside the payload: it is
      // meaningless without the ciphertext, and one opaque envelope cannot be
      // half-migrated by a caller that only knew about one of the two fields.
      const envelope = new Uint8Array(iv.length + ciphertext.byteLength);
      envelope.set(iv, 0);
      envelope.set(new Uint8Array(ciphertext), iv.length);
      return envelope;
    } catch {
      return null;
    }
  }

  async #open(
    key: CryptoKey,
    recordKey: string,
    envelope: ByteArray,
  ): Promise<ByteArray | null> {
    if (envelope.length <= PASSPHRASE_IV_BYTES) return null;
    const iv = envelope.subarray(0, PASSPHRASE_IV_BYTES);
    const ciphertext = envelope.subarray(PASSPHRASE_IV_BYTES);
    try {
      const plaintext = await this.#crypto.subtle.decrypt(
        { name: "AES-GCM", iv: iv as BufferSource, additionalData: encodeUtf8(recordKey) },
        key,
        ciphertext as BufferSource,
      );
      return new Uint8Array(plaintext);
    } catch {
      return null;
    }
  }

  #storedSalt(): ByteArray | null {
    return this.#storedEnvelope(PASSPHRASE_SALT_KEY);
  }

  #storedEnvelope(key: string): ByteArray | null {
    const stored = this.#store.read(key);
    if (stored === undefined || stored === "") return null;
    const bytes = decodeBase64Url(stored);
    return bytes === null || bytes.length === 0 ? null : bytes;
  }
}
